import {
  DISCUSSION_PROFILE_PHOTO_URL,
  fileReviewUserAvatarUrl,
  isSameUser
} from '@jarvis/platform-file-review';
import type { PatientTrackingDiscussionMessage } from './patientTrackingDiscussionTypes';

function buildAliasSet(currentUserId: string, currentUserAliases: Iterable<string>): Set<string> {
  const set = new Set<string>();
  for (const value of currentUserAliases) {
    if (value) set.add(value);
  }
  if (currentUserId && currentUserId !== 'unknown') set.add(currentUserId);
  return set;
}

export interface PtDiscussionChatItem {
  id: string;
  type: 'text';
  text: string;
  timestamp: Date;
  author: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  isEdited?: boolean;
}

/** Trim and lowercase for stable author comparisons (matches server OrdinalIgnoreCase intent). */
export function normalizeUserKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function formatAuthorName(message: PatientTrackingDiscussionMessage): string {
  return message.submittedByDisplayName?.trim() || message.submittedBy?.trim() || 'Someone';
}

function messageAuthorKeys(message: PatientTrackingDiscussionMessage): string[] {
  const keys: string[] = [];
  const userId = message.submittedByUserId?.trim();
  const submittedBy = message.submittedBy?.trim();
  if (userId) keys.push(userId);
  if (submittedBy) keys.push(submittedBy);
  return keys;
}

export function isPtMessageFromCurrentUser(
  message: PatientTrackingDiscussionMessage,
  currentUserId: string,
  currentUserAliases: ReadonlySet<string>
): boolean {
  if (currentUserId === 'unknown' && currentUserAliases.size === 0) return false;

  for (const key of messageAuthorKeys(message)) {
    if (isSameUser(key, currentUserId)) return true;
    for (const alias of currentUserAliases) {
      if (isSameUser(key, alias)) return true;
    }
  }
  return false;
}

export function mapPtMessagesToChatItems(
  messages: PatientTrackingDiscussionMessage[],
  currentUserId: string,
  currentUserAliases: Iterable<string>,
  currentUserHasPhoto: boolean | null | undefined
): PtDiscussionChatItem[] {
  const aliasSet = buildAliasSet(currentUserId, currentUserAliases);

  return messages.map((m) => {
    const isCurrentUser = isPtMessageFromCurrentUser(m, currentUserId, aliasSet);
    const author: PtDiscussionChatItem['author'] = {
      // Match FileReviewChat / DevExtreme demo: user.id === author.id for own messages (strict ===).
      id: isCurrentUser ? currentUserId : messageAuthorKeys(m)[0] ?? m.submittedBy ?? 'unknown',
      name: formatAuthorName(m)
    };
    if (isCurrentUser && currentUserHasPhoto !== false) {
      author.avatarUrl = DISCUSSION_PROFILE_PHOTO_URL;
    } else {
      const peerAvatar = fileReviewUserAvatarUrl(m.submittedByUserId);
      if (peerAvatar) author.avatarUrl = peerAvatar;
    }

    return {
      id: String(m.id),
      type: 'text',
      text: m.text,
      timestamp: new Date(m.submittedAt),
      author,
      isEdited: Boolean(m.updatedAt)
    };
  });
}

export function isOwnPtChatItem(
  item: PtDiscussionChatItem | undefined,
  message: PatientTrackingDiscussionMessage | undefined,
  currentUserId: string,
  currentUserAliases: ReadonlySet<string>
): boolean {
  if (message) return isPtMessageFromCurrentUser(message, currentUserId, currentUserAliases);
  if (!item?.author?.id || currentUserId === 'unknown') return false;
  if (item.author.id === currentUserId) return true;
  if (isSameUser(item.author.id, currentUserId)) return true;
  for (const alias of currentUserAliases) {
    if (isSameUser(item.author.id, alias)) return true;
  }
  return false;
}
